/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Post-login redirect.
 *
 * The ?authToken= hand-off exists for exactly one journey: carrying an
 * authenticated identity ACROSS an origin boundary to the shopping cart.
 *
 * A redirect that stays on the marketing site does not cross an origin
 * boundary, so it must not request a hand-off token — the backend refuses an
 * audience outside ALLOWED_JWT_AUDIENCES and the customer sees a failure
 * message after a sign-in that actually succeeded (T-4a).
 *
 * The backend behaviour is correct and is deliberately left alone. TokenView
 * rejecting an unrecognised audience with 400 is the control that stops a token
 * minted for the cart being replayed against the forum or the chatbot. Widening
 * ALLOWED_JWT_AUDIENCES would also stop the 400 and would weaken a working
 * security control to work around a frontend routing mistake.
 */

import i18n from "i18next";
import { configuration } from "@/lib/configuration";
import { ALL_LANGUAGES, type SupportedLanguage } from "@/lib/constants";
import { redirectWithAuthToken } from "@/lib/http-client";
import { prepareForumAuthenticatedRedirect } from "@/lib/forum-access";
import { LANGUAGE_QUERY_PARAM } from "@/utils/localizedPath";
import { isShopOrigin } from "@/utils/shopOrigins";

/** True only for a genuine cross-origin hop to the shopping cart. */
export function requiresAuthTokenHandoff(targetUrl: string): boolean {
  let target: URL;
  try {
    // Resolved against the current origin so a relative destination such as
    // "/en/download" is handled here explicitly rather than falling through to
    // the catch branch by accident.
    target = new URL(targetUrl, window.location.origin);
  } catch {
    return false; // unparseable — treat as same-origin
  }

  // Same origin as the page we are on: nothing to hand over.
  if (target.origin === window.location.origin) return false;

  // Configured marketing-site origin: same trust domain, no hand-off.
  try {
    if (target.origin === new URL(configuration.websiteUrl).origin) {
      return false;
    }
  } catch {
    /* websiteUrl unset in this environment — fall through */
  }

  // Only a shop origin gets a token (shopOrigins.ts says which).
  //
  // The two checks above look redundant once this one narrows the value to the
  // cart origin, and they are kept deliberately: in an environment where
  // websiteUrl and shoppingCartUrl are configured to the same address, they are
  // what stops a same-trust-domain redirect asking for a token it cannot get.
  return isShopOrigin(target.origin);
}

/**
 * The shop destination, carrying the language this page is showing.
 *
 * N-7 (Session 448): a customer who changes language on the sign-in page and
 * is then returned to the shop arrived in the shop's old language - the shop
 * cannot see a choice made on this site. It reads an explicit `lang` query
 * parameter ahead of its path and its stored preference, so the choice is
 * carried across on the address.
 *
 * The language is the one i18next is SHOWING, not the stored preference:
 * reading storage can throw or be refused in exactly the embedded purchase
 * window this hand-off serves, and the fallback would then answer "en"
 * whatever the customer chose. Only the six site languages are sent; anything
 * else leaves the address as it was and the shop decides as it does today.
 * An existing `lang` on the address is replaced - the page's language is the
 * one the customer is looking at. Harmless when it matches the shop's path.
 */
export function withSiteLanguage(url: string): string {
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "")
    .slice(0, 2)
    .toLowerCase();
  if (!(ALL_LANGUAGES as readonly string[]).includes(lang)) return url;
  let target: URL;
  try {
    target = new URL(url, window.location.origin);
  } catch {
    return url;
  }
  target.searchParams.set(LANGUAGE_QUERY_PARAM, lang as SupportedLanguage);
  return target.toString();
}

export type RedirectResult =
  | { ok: true }
  | { ok: false; reason: "token_failed"; cause?: "no_session" };

export interface RedirectOptions {
  /**
   * Replace the current history entry instead of adding one.
   *
   * REQUIRED wherever the address the browser is currently on carries a
   * session key. OAuthComplete sits on exactly such an address, and used
   * window.location.replace() for that reason: an ordinary navigation would
   * leave a live session key in the browser's back history for anybody with
   * access to that machine.
   */
  replace?: boolean;
}

/**
 * Single exit point for every successful authentication on this site.
 *
 * P-1: the shared forum session is prepared HERE, at the exit point, rather
 * than as a side effect of browsing the marketing site. It used to be created
 * by a header hook, which only runs when the customer is already on the
 * website — so a customer who pressed Sign In on the forum signed in
 * successfully and was returned to the forum still shown as a guest, and only
 * a later visit to the website fixed it. That is why the fault looked
 * intermittent rather than broken.
 *
 * The preparation step cannot throw and cannot block: it reads the stored
 * session key rather than asking the server, and it returns the original
 * destination unchanged whenever it cannot help. A sign-in that succeeded
 * always ends in a navigation.
 */
export async function completeLoginRedirect(
  url: string,
  options: RedirectOptions = {},
): Promise<RedirectResult> {
  if (requiresAuthTokenHandoff(url)) {
    // N-3 (Session 447): this function is called from exactly one kind of
    // place - immediately after a successful authentication - so it can state
    // that a session is expected, and the helper can then treat a missing key
    // as the failure it is rather than as a signed-out visitor.
    //
    // N-7 (Session 448): the shop branch only. The forum and on-site
    // destinations below carry their own language in their paths.
    return redirectWithAuthToken(withSiteLanguage(url), {
      sessionExpected: true,
    });
  }

  const destination = prepareForumAuthenticatedRedirect(url);

  // The forum branch always replaces, whatever the caller asked for: the
  // display-name screen performs a full navigation on to the forum, so leaving
  // it in the back history gives the customer a page they cannot get back
  // past — pressing Back from the forum would land on the bridge, which
  // creates the session again and sends them forward again.
  const replace = options.replace || destination !== url;

  if (replace) {
    window.location.replace(destination);
  } else {
    window.location.href = destination;
  }
  return { ok: true };
}
